import { ContextualInfoButton, ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { HubFormItem } from "@/components/inputs/HubFormItem";
import { getArchetypeByValue, getArchetypes, getDefaultInputValues } from "@/features/branding";
import { defaultBuildYear, defaultInputValues } from "@/features/risk_bands";
import { keys } from "@/tools/aggregate/portfolio-export/schema";
import { LargeSiteSettings, LargeSiteSettingsRefs } from "@/tools/create/lss-from-map/components/LargeSiteSettings";
import { LargeSiteSettingsValues, PointWithMetadata } from "@/tools/create/lss-from-map/types";
import { RiskBandsArchetype } from "@/tools/risk-bands/types";
import { handoffToLargeSiteScreening } from "@/utils/handoff";
import { Button, Col, Collapse, Divider, Form, InputNumber, Row, Select, Switch, Typography } from "antd";
import { Polygon } from "leaflet";
import { MutableRefObject, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toPerPoint, toWholeSite } from "../utils";
const {
  Title
} = Typography;
interface SettingsStageSidebarProps {
  onGoBack: () => void;
  points: PointWithMetadata[];
  polygons: Polygon[];
  financialSectionOpen?: boolean;
  refs?: {
    settingsContent?: MutableRefObject<null>;
    settingsScrollArea?: MutableRefObject<HTMLDivElement | null>;
    financialSectionNarrow?: MutableRefObject<HTMLDivElement | null>;
    financialSectionWide?: MutableRefObject<HTMLDivElement | null>;
    proceedToReviewBtn?: MutableRefObject<null>;
    onSettingsRefs?: (refs: LargeSiteSettingsRefs) => void;
  };
}
const valueDividerOptions = [{
  label: "For whole site",
  value: "whole site"
}, {
  label: "Per individual grid point",
  value: "per grid point"
}];
const fireProtectionOptions = [{
  value: "none",
  label: "None"
}, {
  value: "heatEmber",
  label: "Heat Ember"
}, {
  value: "flame",
  label: "Flame"
}];
const foundationDesignOptions = [{
  value: "normal",
  label: "Normal"
}, {
  value: "rigid",
  label: "Rigid"
}];
const MAX_WHOLE_COST_VALUE = 100000000000; // 100 billion
const MAX_PER_POINT_COST_VALUE = 40000000; // 40 million

const collapseClassName = "[&_.ant-collapse-content-box]:!px-0 [&_.ant-collapse-header]:!px-0";

/** A threshold parameter the user can supply (mirrors Customise Template options). */
type ThresholdOption = {
  label: string;
  value: keyof typeof keys;
  metric: string;
  note: string;
  validate: (value: number) => boolean;
};

/** Switch + title (+ optional subtitle/info), with the input field beneath. */
const AdvancedSetting = ({
  title,
  subtitle,
  info,
  enabled,
  onToggle,
  children
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  info?: ReactNode;
  enabled: boolean;
  onToggle: (value: boolean) => void;
  children: ReactNode;
}) => <div data-sentry-component="AdvancedSetting" data-sentry-source-file="SettingsStageSidebar.tsx">
    <div className="flex items-center gap-2">
      <Switch size="small" checked={enabled} onChange={onToggle} data-sentry-element="Switch" data-sentry-source-file="SettingsStageSidebar.tsx" />
      <span className="text-sm font-medium text-gray-700">{title}</span>
      {info}
    </div>
    {subtitle && <div className="ml-9 mt-0.5 text-xs text-gray-500">{subtitle}</div>}
    <div className="ml-9 mt-1">{children}</div>
  </div>;

/** Switch + title, with a parameter selector and value input (mirrors Customise Template). */
const AdvancedThresholdSetting = ({
  title,
  info,
  enabled,
  onToggle,
  options,
  param,
  onParamChange,
  value,
  onValueChange,
  error
}: {
  title: ReactNode;
  info?: ReactNode;
  enabled: boolean;
  onToggle: (value: boolean) => void;
  options: ThresholdOption[];
  param: keyof typeof keys;
  onParamChange: (value: keyof typeof keys) => void;
  value?: number;
  onValueChange: (value?: number) => void;
  error?: string;
}) => {
  const selected = options.find(option => option.value === param);
  return <div data-sentry-component="AdvancedThresholdSetting" data-sentry-source-file="SettingsStageSidebar.tsx">
      <div className="flex items-center gap-2">
        <Switch size="small" checked={enabled} onChange={onToggle} data-sentry-element="Switch" data-sentry-source-file="SettingsStageSidebar.tsx" />
        <span className="text-sm font-medium text-gray-700">{title}</span>
        {info}
      </div>
      <div className="ml-9 mt-1 flex flex-col gap-1">
        <Select disabled={!enabled} size="small" options={options} value={param} onChange={value => onParamChange(value as keyof typeof keys)} data-sentry-element="Select" data-sentry-source-file="SettingsStageSidebar.tsx" />
        <InputNumber disabled={!enabled} size="small" className="w-full" placeholder={selected?.metric} value={value} onChange={value => onValueChange(value ?? undefined)} status={error ? "error" : undefined} data-sentry-element="InputNumber" data-sentry-source-file="SettingsStageSidebar.tsx" />
        {enabled && <div className={`text-xs ${error ? "text-red-600" : "text-gray-500"}`}>{error || selected?.note}</div>}
      </div>
    </div>;
};
export const SettingsStageSidebar = ({
  onGoBack,
  points,
  polygons,
  financialSectionOpen,
  refs
}: SettingsStageSidebarProps) => {
  const {
    t
  } = useTranslation("terms");
  const archetypes = getArchetypes();
  const defaultArchetype = archetypes.find(({
    id
  }) => id === "generic-industrial") || archetypes[0];
  const [analysisSettings, setAnalysisSettings] = useState<LargeSiteSettingsValues>({
    archetype: defaultArchetype.value as RiskBandsArchetype,
    buildYear: {
      source: "archetype",
      amount: defaultArchetype.defaultBuildYear || defaultBuildYear
    },
    heightAboveGround: {
      source: "archetype",
      amount: defaultArchetype.defaultHeightAboveGround || 0
    }
  });
  const [valueDivider, setValueDivider] = useState(valueDividerOptions[0].value);
  const defaultMarketValue = defaultInputValues.aud.residential.marketValue;
  const defaultReplacementCost = defaultInputValues.aud.residential.replacementCost;
  const defaultMortgageRate = defaultInputValues.aud.residential.mortgageRate;
  const [marketValue, setMarketValue] = useState<number | null>(defaultMarketValue);
  const [replacementCost, setReplacementCost] = useState<number | null>(defaultReplacementCost);
  const [mortgageRate, setMortgageRate] = useState(defaultMortgageRate);

  // Advanced input settings — each is opt-in via its switch and disabled by default.
  const [elevationEnabled, setElevationEnabled] = useState(false);
  const [elevation, setElevation] = useState<number | undefined>(undefined);
  const [heatEnabled, setHeatEnabled] = useState(false);
  const [heatParam, setHeatParam] = useState<keyof typeof keys>("heatThresholdReturnPeriod");
  const [heatValue, setHeatValue] = useState<number | undefined>(undefined);
  const [windEnabled, setWindEnabled] = useState(false);
  const [windParam, setWindParam] = useState<keyof typeof keys>("windThresholdReturnPeriod");
  const [windValue, setWindValue] = useState<number | undefined>(undefined);
  const [tcwEnabled, setTcwEnabled] = useState(false);
  const [tcwParam, setTcwParam] = useState<keyof typeof keys>("tcwThresholdReturnPeriod");
  const [tcwValue, setTcwValue] = useState<number | undefined>(undefined);
  const [foundationDesignEnabled, setFoundationDesignEnabled] = useState(false);
  const [foundationDesign, setFoundationDesign] = useState(foundationDesignOptions[0].value);
  const [fireProtectionEnabled, setFireProtectionEnabled] = useState(false);
  const [fireProtection, setFireProtection] = useState(fireProtectionOptions[0].value);
  const heatThresholdOptions: ThresholdOption[] = [{
    label: t("returnPeriod"),
    value: "heatThresholdReturnPeriod",
    metric: "number",
    note: "System accepts values greater than 1",
    validate: value => value > 1
  }, {
    label: t("returnFrequency"),
    value: "heatThresholdReturnFrequency",
    metric: "decimal",
    note: "System accepts values 0<x<1",
    validate: value => value > 0 && value < 1
  }, {
    label: t("temperature"),
    value: "heatThresholdTemperature",
    metric: t("units.celsius"),
    note: "System accepts values between 26 - 52 C",
    validate: value => value >= 26 && value <= 52
  }];
  const windThresholdOptions: ThresholdOption[] = [{
    label: t("returnPeriod"),
    value: "windThresholdReturnPeriod",
    metric: "number",
    note: "System accepts values greater than 1",
    validate: value => value > 1
  }, {
    label: t("returnFrequency"),
    value: "windThresholdReturnFrequency",
    metric: "decimal",
    note: "System accepts values 0<x<1",
    validate: value => value > 0 && value < 1
  }];
  const tcwThresholdOptions: ThresholdOption[] = [{
    label: t("returnPeriod"),
    value: "tcwThresholdReturnPeriod",
    metric: "number",
    note: "System accepts values greater than 1",
    validate: value => value > 1
  }, {
    label: t("returnFrequency"),
    value: "tcwThresholdReturnFrequency",
    metric: "decimal",
    note: "System accepts values 0<x<1",
    validate: value => value > 0 && value < 1
  }, {
    label: t("windSpeed"),
    value: "tcwThresholdWindSpeed",
    metric: t("units.metresPerSecond"),
    note: "System accepts values 0<x<120",
    validate: value => value > 0 && value < 120
  }];
  useEffect(() => {
    const archetype = archetypes.find(({
      value
    }) => value === analysisSettings.archetype);
    if (!archetype) {
      return;
    }
    const {
      marketValue,
      replacementCost,
      mortgageRate: newMortgageRate
    } = getDefaultInputValues(archetype.value);
    setMarketValue(marketValue);
    setReplacementCost(replacementCost);
    setMortgageRate(newMortgageRate);
  }, [analysisSettings.archetype]);

  /** Optional advanced columns to apply verbatim to every grid point. */
  const buildAdvancedProps = () => {
    const props: Record<string, string | number> = {};
    if (elevationEnabled && elevation != null) {
      props[keys.elevation] = elevation;
    }
    if (heatEnabled && heatValue != null) {
      props[keys[heatParam]] = heatValue;
    }
    if (windEnabled && windValue != null) {
      props[keys[windParam]] = windValue;
    }
    if (tcwEnabled && tcwValue != null) {
      props[keys[tcwParam]] = tcwValue;
    }
    if (foundationDesignEnabled) {
      props[keys.foundationDesign] = foundationDesign;
    }
    if (fireProtectionEnabled) {
      props[keys.fireProtection] = fireProtection;
    }
    return props;
  };
  const startRun = () => {
    const advancedProps = buildAdvancedProps();
    const handoffData = {
      assets: points.map(({
        point
      }) => ({
        [keys.id]: point.properties?.name,
        [keys.latitude]: point.geometry.coordinates[1],
        [keys.longitude]: point.geometry.coordinates[0],
        [keys.archetype]: (analysisSettings.archetype ? getArchetypeByValue(analysisSettings.archetype)?.name : undefined) as any,
        [keys.buildYear]: analysisSettings.buildYear.amount,
        [keys.floorHeight]: analysisSettings.heightAboveGround.amount,
        [keys.marketValue]: (marketValue ?? defaultMarketValue) / points.length,
        [keys.replacementCost]: (replacementCost ?? defaultReplacementCost) / points.length,
        [keys.mortgageRate]: mortgageRate,
        ...advancedProps
      })),
      polygons
    };
    handoffToLargeSiteScreening(handoffData);
  };
  const commaFormatter = (value?: number) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const [estMarketValueError, setEstMarketValueError] = useState<string | undefined>(undefined);
  const [estReplacementCostError, setEstReplacementCostError] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!marketValue) {
      setEstMarketValueError("Est. Market Value is required.");
    } else if (valueDivider === "whole site") {
      setEstMarketValueError(marketValue > MAX_WHOLE_COST_VALUE ? `Est. Market Value cannot exceed ${commaFormatter(MAX_WHOLE_COST_VALUE)}` : undefined);
    } else {
      setEstMarketValueError(toPerPoint(marketValue, points.length) > MAX_PER_POINT_COST_VALUE ? `Est. Market Value cannot exceed ${commaFormatter(MAX_PER_POINT_COST_VALUE)}` : undefined);
    }
  }, [valueDivider, marketValue]);
  useEffect(() => {
    if (!replacementCost) {
      setEstReplacementCostError("Est. Replacement Cost is required.");
    } else if (valueDivider === "whole site") {
      setEstReplacementCostError(replacementCost > MAX_WHOLE_COST_VALUE ? `Est. Replacement Cost cannot exceed ${commaFormatter(MAX_WHOLE_COST_VALUE)}` : undefined);
    } else {
      setEstReplacementCostError(toPerPoint(replacementCost, points.length) > MAX_PER_POINT_COST_VALUE ? `Est. Replacement cost cannot exceed ${commaFormatter(MAX_PER_POINT_COST_VALUE)}` : undefined);
    }
  }, [valueDivider, replacementCost]);
  const thresholdError = (enabled: boolean, value: number | undefined, options: ThresholdOption[], param: keyof typeof keys) => {
    if (!enabled) {
      return undefined;
    }
    if (value == null) {
      return "Please enter a value.";
    }
    const option = options.find(option => option.value === param);
    return option && !option.validate(value) ? option.note : undefined;
  };
  const elevationError = elevationEnabled && elevation == null ? "Please enter a value." : undefined;
  const heatError = thresholdError(heatEnabled, heatValue, heatThresholdOptions, heatParam);
  const windError = thresholdError(windEnabled, windValue, windThresholdOptions, windParam);
  const tcwError = thresholdError(tcwEnabled, tcwValue, tcwThresholdOptions, tcwParam);
  const hasError = !!estMarketValueError || !!estReplacementCostError || !!elevationError || !!heatError || !!windError || !!tcwError;

  // ─── Reusable section content (shared between narrow and wide layouts) ───

  const financialContent = <>
      <Select className="w-full" onSelect={setValueDivider} options={valueDividerOptions} value={valueDivider} />
      <Form layout="vertical" className="mt-2">
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <HubFormItem label={<ContextualInfoButtonWithText id="marketValue">Est. Market Value</ContextualInfoButtonWithText>} className="[&_.ant-form-item-explain-error]:!text-xs" hasFeedback error={estMarketValueError ? {
            message: estMarketValueError
          } : undefined}>
              <InputNumber addonAfter={valueDivider} className="w-full" formatter={commaFormatter} onChange={value => {
              setMarketValue(value === null ? null : valueDivider === "whole site" ? value : toWholeSite(value, points.length));
            }} value={marketValue === null ? null : valueDivider === "whole site" ? marketValue : toPerPoint(marketValue, points.length)} />
              <div className="mt-1 text-xs text-gray-400">
                {valueDivider === "whole site" ? `Equates to ${commaFormatter(toPerPoint(marketValue ?? 0, points.length))} per grid point` : `Equates to ${commaFormatter(marketValue ?? 0)} for the whole site`}
              </div>
            </HubFormItem>
          </Col>
          <Col span={24}>
            <HubFormItem label={<ContextualInfoButtonWithText id="replacementCost">Est. Replacement Cost</ContextualInfoButtonWithText>} className="[&_.ant-form-item-explain-error]:!text-xs" hasFeedback error={estReplacementCostError ? {
            message: estReplacementCostError
          } : undefined}>
              <InputNumber addonAfter={valueDivider} className="w-full" formatter={commaFormatter} onChange={value => {
              setReplacementCost(value === null ? null : valueDivider === "whole site" ? value : toWholeSite(value, points.length));
            }} value={replacementCost === null ? null : valueDivider === "whole site" ? replacementCost : toPerPoint(replacementCost, points.length)} />
              <div className="mt-1 text-xs text-gray-400">
                {valueDivider === "whole site" ? `Equates to ${commaFormatter(toPerPoint(replacementCost ?? 0, points.length))} per grid point` : `Equates to ${commaFormatter(replacementCost ?? 0)} for the whole site`}
              </div>
            </HubFormItem>
          </Col>
          <Col span={24}>
            <HubFormItem label={<ContextualInfoButtonWithText id="mortgageRate">Mortgage Rate</ContextualInfoButtonWithText>}>
              <InputNumber addonAfter="%" className="w-full" min={0} step={0.1} value={mortgageRate * 100} onChange={value => setMortgageRate((value ?? defaultMortgageRate * 100) / 100)} />
            </HubFormItem>
          </Col>
        </Row>
      </Form>
    </>;
  const advancedContent = <div className="flex flex-col gap-4">
      <AdvancedSetting title={t("elevation")} subtitle="Will override third-party elevation data. Applied verbatim to all points." info={<ContextualInfoButton rootClassName="!inline-flex !text-gray-500 !mt-0" id="elevation" size="small" />} enabled={elevationEnabled} onToggle={setElevationEnabled}>
        <InputNumber disabled={!elevationEnabled} size="small" className="w-full" placeholder={t("metres") ?? undefined} value={elevation} onChange={value => setElevation(value ?? undefined)} status={elevationError ? "error" : undefined} />
        {elevationError && <div className="mt-1 text-xs text-red-600">{elevationError}</div>}
      </AdvancedSetting>

      <AdvancedThresholdSetting title={`${t("hazards.heatFailure")} ${t("threshold")}`} info={<ContextualInfoButton rootClassName="!inline-flex !text-gray-500 !mt-0" id="heatThreshold" size="small" />} enabled={heatEnabled} onToggle={setHeatEnabled} options={heatThresholdOptions} param={heatParam} onParamChange={setHeatParam} value={heatValue} onValueChange={setHeatValue} error={heatError} />

      <AdvancedThresholdSetting title={`${t("hazards.extremeWind")} ${t("threshold")}`} info={<ContextualInfoButton rootClassName="!inline-flex !text-gray-500 !mt-0" id="windThreshold" size="small" />} enabled={windEnabled} onToggle={setWindEnabled} options={windThresholdOptions} param={windParam} onParamChange={setWindParam} value={windValue} onValueChange={setWindValue} error={windError} />

      <AdvancedThresholdSetting title={`${t("hazards.cycloneWind")} ${t("threshold")}`} info={<ContextualInfoButton rootClassName="!inline-flex !text-gray-500 !mt-0" id="tcwThreshold" size="small" />} enabled={tcwEnabled} onToggle={setTcwEnabled} options={tcwThresholdOptions} param={tcwParam} onParamChange={setTcwParam} value={tcwValue} onValueChange={setTcwValue} error={tcwError} />

      <AdvancedSetting title={<ContextualInfoButtonWithText id="foundationDesign" rootClassName="!inline-flex !text-gray-500" size="small">
            {t("foundationDesign")}
          </ContextualInfoButtonWithText>} enabled={foundationDesignEnabled} onToggle={setFoundationDesignEnabled}>
        <Select disabled={!foundationDesignEnabled} size="small" className="w-full" options={foundationDesignOptions} value={foundationDesign} onSelect={setFoundationDesign} />
      </AdvancedSetting>

      <AdvancedSetting title={<ContextualInfoButtonWithText id="fireProtection" rootClassName="!inline-flex !text-gray-500" size="small">
            {t("fireProtection")}
          </ContextualInfoButtonWithText>} enabled={fireProtectionEnabled} onToggle={setFireProtectionEnabled}>
        <Select disabled={!fireProtectionEnabled} size="small" className="w-full" options={fireProtectionOptions} value={fireProtection} onSelect={setFireProtection} />
      </AdvancedSetting>
    </div>;
  const financialSectionTitle = <Title className="!mb-0" level={5}>
      Financial Settings
    </Title>;
  const assetSettingsSubtitle = "Input settings provided will be applied to all grid points within the Large Site polygon.";
  return <div className="flex max-h-[70vh] flex-col" ref={refs?.settingsContent} data-sentry-component="SettingsStageSidebar" data-sentry-source-file="SettingsStageSidebar.tsx">
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-3" ref={refs?.settingsScrollArea}>
        {/* ─── Narrow: single column (hidden on xl+) ─── */}
        <div className="xl:hidden">
          <Row gutter={[16, 16]} className="py-2" data-sentry-element="Row" data-sentry-source-file="SettingsStageSidebar.tsx">
            <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
              <Title level={4} className="!mb-1" data-sentry-element="Title" data-sentry-source-file="SettingsStageSidebar.tsx">
                Asset Settings
              </Title>
              <p className="m-0 text-xs text-gray-500">{assetSettingsSubtitle}</p>
            </Col>

            <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
              <Title className="!mb-1" level={5} data-sentry-element="Title" data-sentry-source-file="SettingsStageSidebar.tsx">
                Basic Design Settings
              </Title>
              <p className="m-0 mb-2 text-xs text-gray-500">All fields are required</p>
              <LargeSiteSettings values={analysisSettings} setValues={setAnalysisSettings} setRefs={refs?.onSettingsRefs} data-sentry-element="LargeSiteSettings" data-sentry-source-file="SettingsStageSidebar.tsx" />
            </Col>

            <Divider className="!my-0 mx-2" data-sentry-element="Divider" data-sentry-source-file="SettingsStageSidebar.tsx" />

            <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
              <Collapse ghost className={collapseClassName} items={[{
              key: "advanced",
              label: <Title className="!mb-0" level={5}>
                        Advanced Design Settings
                      </Title>,
              children: <>
                        <p className="m-0 pb-2 text-xs text-gray-500">All fields are optional</p>
                        {advancedContent}
                      </>
            }]} data-sentry-element="Collapse" data-sentry-source-file="SettingsStageSidebar.tsx" />
            </Col>

            <Divider className="!my-0 mx-2" data-sentry-element="Divider" data-sentry-source-file="SettingsStageSidebar.tsx" />

            <Col span={24} data-sentry-element="Col" data-sentry-source-file="SettingsStageSidebar.tsx">
              <div ref={refs?.financialSectionNarrow}>
                <Collapse ghost activeKey={financialSectionOpen ? "financial" : undefined} className={collapseClassName} items={[{
                key: "financial",
                collapsible: "header",
                label: <Title className="!mb-0" level={5}>
                          Financial Settings
                        </Title>,
                children: financialContent
              }]} data-sentry-element="Collapse" data-sentry-source-file="SettingsStageSidebar.tsx" />
              </div>
            </Col>
          </Row>
        </div>

        {/* ─── Wide: two columns (shown on xl+) ─── */}
        <div className="hidden xl:grid xl:grid-cols-2 xl:gap-x-6 xl:py-2">
          {/* Column 1: Basic Design Settings + Financial Settings */}
          <div className="flex flex-col gap-4">
            <div>
              <Title level={4} className="!mb-1" data-sentry-element="Title" data-sentry-source-file="SettingsStageSidebar.tsx">
                Asset Settings
              </Title>
              <p className="m-0 mb-2 text-xs text-gray-500">{assetSettingsSubtitle}</p>
              <Title className="!mb-1" level={5} data-sentry-element="Title" data-sentry-source-file="SettingsStageSidebar.tsx">
                Basic Design Settings
              </Title>
              <p className="m-0 mb-4 text-xs text-gray-500">All fields are required</p>
              <LargeSiteSettings values={analysisSettings} setValues={setAnalysisSettings} setRefs={refs?.onSettingsRefs} data-sentry-element="LargeSiteSettings" data-sentry-source-file="SettingsStageSidebar.tsx" />
            </div>
            <Divider className="!my-0" data-sentry-element="Divider" data-sentry-source-file="SettingsStageSidebar.tsx" />
            <div ref={refs?.financialSectionWide}>
              {financialSectionTitle}
              <div className="mt-2">{financialContent}</div>
            </div>
          </div>

          {/* Column 2: Advanced Design Settings */}
          <div className="flex flex-col gap-4 pt-[4.5rem]">
            <div>
              <Title className="!mb-1" level={5} data-sentry-element="Title" data-sentry-source-file="SettingsStageSidebar.tsx">
                Advanced Design Settings
              </Title>
              <p className="m-0 text-xs text-gray-500">All fields are optional</p>
            </div>
            {advancedContent}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1 border-t border-gray-200 pr-3 pt-2">
        <div ref={refs?.proceedToReviewBtn}>
          <Button block type="primary" disabled={hasError} onClick={startRun} data-sentry-element="Button" data-sentry-source-file="SettingsStageSidebar.tsx">
            Proceed to Review
          </Button>
        </div>
        <Button block onClick={onGoBack} data-sentry-element="Button" data-sentry-source-file="SettingsStageSidebar.tsx">
          Go Back
        </Button>
      </div>
    </div>;
};